// OLMPS — Trajectory view (mobile ledger item #2).
//
// Per docs/OLMPS_SYSTEM.md's daily-loop model: "tap a task → see trajectory.
// Trajectory means task becomes an orb and there is a direct line connecting
// task orb to endeavour orb." This is the minimal viable version of that:
// a full-screen sheet showing the chain Task → Aim → Goal → Endeavour as
// connected orbs with a single SVG line running through them.
//
// Data source: user_settings.oracle_spine via loadOracleSpine (read-only
// import from oracle-spine-data.ts — this file does not write the spine).
// Sector resolution: the caller (MetisTool) hands this view the agenda
// item's containing PROFILE label (its closest analog to "the task's list"
// — METIS agenda items carry no list/sector column of their own, unlike
// KYRS's task_lists.olmps_sector). If that label case-insensitively matches
// a SHAPE sector label (the same "does this name read as a sector" heuristic
// CompassView.tsx / useTodayData.ts already use for task groups and agenda
// text), the trajectory resolves through that sector's spine node. Items
// with no profile (the plain day agenda) or a profile label that doesn't
// read as a sector fall back to the "unlinked" state: just the task orb and
// a hint that it isn't linked to a sector yet.

import React, { useEffect, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Svg, { Line } from 'react-native-svg';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { useTheme, makeThemedStyles, DISPLAY_FONT, SECTOR_COLOR, radii } from '../v10/theme';
import { SECTORS, SECTOR_LABELS } from '../domain/olmps';
import type { Sector } from '../domain/olmps';
import { effectiveAims, loadOracleSpine, SECTOR_DEITY_MAP, type OracleSpineNode } from './oracle-spine-data';
import { Atmosphere, GlassPanel } from './glass';

// Per-sector palette comes from the shared v10 SECTOR_COLOR.
const SECTOR_ORB_COLOR = SECTOR_COLOR;

// Same case-insensitive "contains" match useTodayData.ts/CompassView.tsx use
// to line up a free-text group/profile label against a SHAPE sector label.
function nameMatchesSector(name: string, sectorLabel: string): boolean {
  const a = name.trim().toLowerCase();
  const b = sectorLabel.trim().toLowerCase();
  if (!a || !b) return false;
  return a.includes(b) || b.includes(a);
}

function resolveSectorFromLabel(label: string | null | undefined): Sector | null {
  if (!label) return null;
  for (const sector of SECTORS) {
    if (nameMatchesSector(label, SECTOR_LABELS[sector])) return sector;
  }
  return null;
}

interface TrajectoryOrb {
  key: string;
  title: string;
  subtitle: string;
  color: string;
  filled: boolean; // false = "not set yet" placeholder orb
}

function buildChain(taskText: string, sector: Sector | null, node: OracleSpineNode | null): TrajectoryOrb[] {
  const { c } = useTheme();
  const taskOrb: TrajectoryOrb = {
    key: 'task',
    title: taskText || 'This task',
    subtitle: 'Task',
    color: c.primary,
    filled: true,
  };

  if (!sector || !node) return [taskOrb];

  const sectorColor = SECTOR_ORB_COLOR[sector];

  const aims = effectiveAims(node);
  const aimOrb: TrajectoryOrb = aims.length > 0
    ? {
        key: 'aim',
        title: aims.length === 1 ? aims[0].label : `${aims[0].label} + ${aims.length - 1} more`,
        subtitle: 'Aim',
        color: sectorColor,
        filled: true,
      }
    : { key: 'aim', title: 'No aim actions set yet', subtitle: 'Aim', color: sectorColor, filled: false };

  const deity = node.goal ? SECTOR_DEITY_MAP[sector].find((d) => d.id === node.goal) : undefined;
  const goalOrb: TrajectoryOrb = deity
    ? { key: 'goal', title: deity.name, subtitle: 'Goal', color: sectorColor, filled: true }
    : { key: 'goal', title: 'No goal set yet', subtitle: 'Goal', color: sectorColor, filled: false };

  const endeavourOrb: TrajectoryOrb = node.endeavour
    ? { key: 'endeavour', title: node.endeavour, subtitle: `Endeavour · ${SECTOR_LABELS[sector]}`, color: sectorColor, filled: true }
    : { key: 'endeavour', title: 'No endeavour set yet', subtitle: `Endeavour · ${SECTOR_LABELS[sector]}`, color: sectorColor, filled: false };

  return [taskOrb, aimOrb, goalOrb, endeavourOrb];
}

const ORB_SIZE = 64;
const ROW_HEIGHT = 116;

interface TrajectoryViewProps {
  visible: boolean;
  onClose: () => void;
  /** The tapped task/agenda item's display text — becomes the Task orb. */
  taskText: string;
  /**
   * The task's containing list/profile label, if any — this view resolves
   * it against SHAPE sector labels to find the sector's spine node. Pass
   * null/undefined for tasks with no containing list (e.g. the plain day
   * agenda) — the view falls back to the unlinked state.
   */
  listLabel?: string | null;
}

/** Trajectory overlay: Task orb → Aim orb → Goal orb → Endeavour orb. */
export function TrajectoryView({ visible, onClose, taskText, listLabel }: TrajectoryViewProps) {
  const styles = useStyles();
  const { c } = useTheme();
  const { session } = useAuth();
  const userId = session?.user.id;

  const [loading, setLoading] = useState(false);
  const [node, setNode] = useState<OracleSpineNode | null>(null);

  const sector = resolveSectorFromLabel(listLabel);

  useEffect(() => {
    if (!visible || !userId || !sector) {
      setNode(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const spine = await loadOracleSpine(supabase, userId);
        if (!cancelled) setNode(spine[sector]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, userId, sector]);

  const chain = buildChain(taskText, sector, node);
  const unlinked = chain.length === 1;

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <View style={styles.root}>
        <Atmosphere />
        <View style={styles.headerBar}>
          <Text style={styles.headerTitle}>Trajectory</Text>
          <Pressable onPress={onClose} hitSlop={8}>
            <Text style={styles.headerAction}>Done</Text>
          </Pressable>
        </View>
        <ScrollView contentContainerStyle={styles.scrollContent}>
          {loading ? (
            <Text style={styles.hint}>Loading trajectory…</Text>
          ) : (
            <View style={styles.chainWrap}>
              <Svg
                width="100%"
                height={ROW_HEIGHT * chain.length}
                style={StyleSheet.absoluteFill}
                pointerEvents="none"
              >
                {chain.length > 1 ? (
                  <Line
                    x1="50%"
                    x2="50%"
                    y1={ROW_HEIGHT / 2}
                    y2={ROW_HEIGHT * (chain.length - 1) + ROW_HEIGHT / 2}
                    stroke={c.blueText}
                    strokeOpacity={0.35}
                    strokeWidth={2}
                    strokeDasharray="6 6"
                  />
                ) : null}
              </Svg>
              {chain.map((orb) => (
                <View key={orb.key} style={styles.orbRow}>
                  <View
                    style={[
                      styles.orb,
                      { borderColor: orb.color, backgroundColor: orb.filled ? orb.color : c.glassFillStrong },
                    ]}
                  >
                    <Text
                      style={[styles.orbGlyph, { color: orb.filled ? c.white : orb.color }]}
                      numberOfLines={2}
                    >
                      {orb.subtitle}
                    </Text>
                  </View>
                  <GlassPanel style={styles.orbCard} contentStyle={styles.orbCardContent}>
                    <Text style={styles.orbSubtitle}>{orb.subtitle}</Text>
                    <Text style={styles.orbTitle} numberOfLines={2}>{orb.title}</Text>
                  </GlassPanel>
                </View>
              ))}
            </View>
          )}

          {!loading && unlinked ? (
            <Text style={styles.hint}>
              No trajectory — not linked to a sector list. Move this task into a sector-named list
              (Social, Health, Admin, Personal, Economic) to see its Aim → Goal → Endeavour chain.
            </Text>
          ) : null}
        </ScrollView>
      </View>
    </Modal>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  root: { flex: 1 },
  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingTop: 18,
    paddingBottom: 12,
  },
  headerTitle: { fontSize: 17, fontWeight: '800', color: c.ink, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  headerAction: { fontSize: 15, fontWeight: '700', color: c.blueText },
  scrollContent: { paddingHorizontal: 18, paddingBottom: 32, gap: 16 },
  chainWrap: { position: 'relative' },
  orbRow: {
    height: ROW_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  orb: {
    width: ORB_SIZE,
    height: ORB_SIZE,
    borderRadius: ORB_SIZE / 2,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 6,
  },
  orbGlyph: { fontSize: 10, fontWeight: '800', textAlign: 'center' },
  orbCard: { flex: 1 },
  orbCardContent: { gap: 2 },
  orbSubtitle: { fontSize: 11, fontWeight: '700', color: c.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 },
  orbTitle: { fontSize: 15, fontWeight: '700', color: c.ink, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  hint: { fontSize: 13, color: c.textMuted, lineHeight: 19, marginTop: 8 },
}),
);
